import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { CheckCircle, Loader2, Send } from 'lucide-react';
import {
  LIVING_WEBSITE_ENTERPRISE_BUDGET_RANGES,
  LIVING_WEBSITE_ENTERPRISE_COMPANY_SIZES,
} from '../../lib/livingWebsiteProducts';

export type EnterpriseInquiryIntent = 'talk' | 'demo';

interface LivingWebsiteEnterpriseFormProps {
  intent?: EnterpriseInquiryIntent;
}

interface EnterpriseFormData {
  name: string;
  company: string;
  email: string;
  website: string;
  companySize: string;
  monthlyVisitors: string;
  productsBrandsLocations: string;
  primaryUseCase: string;
  requiredIntegrations: string;
  launchTimeline: string;
  budgetRange: string;
  intent: EnterpriseInquiryIntent;
  consent: boolean;
}

const FORM_NAME = 'living-website-enterprise';

const emptyForm = (intent: EnterpriseInquiryIntent): EnterpriseFormData => ({
  name: '',
  company: '',
  email: '',
  website: '',
  companySize: '',
  monthlyVisitors: '',
  productsBrandsLocations: '',
  primaryUseCase: '',
  requiredIntegrations: '',
  launchTimeline: '',
  budgetRange: '',
  intent,
  consent: false,
});

const fieldClass =
  'w-full rounded-lg border border-white/15 bg-white/[0.04] px-4 py-3 text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/25';
const selectClass = `${fieldClass} [color-scheme:dark]`;
const optionClass = 'bg-[#111111] text-white';
const labelClass = 'mb-2 block text-sm font-medium text-white/70';
const errorClass = 'mt-1 text-sm text-red-400';

function encodeNetlifyBody(data: Record<string, string>): string {
  return new URLSearchParams(data).toString();
}

export default function LivingWebsiteEnterpriseForm({
  intent = 'talk',
}: LivingWebsiteEnterpriseFormProps) {
  const [formData, setFormData] = useState<EnterpriseFormData>(() => emptyForm(intent));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, intent }));
  }, [intent]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checkbox.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateForm = () => {
    const next: Record<string, string> = {};
    if (!formData.name.trim()) next.name = 'Name is required';
    if (!formData.company.trim()) next.company = 'Company is required';
    if (!formData.email.trim()) {
      next.email = 'Work email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      next.email = 'Enter a valid work email';
    }
    if (!formData.primaryUseCase.trim()) next.primaryUseCase = 'Primary use case is required';
    if (!formData.budgetRange) next.budgetRange = 'Budget range is required';
    if (!formData.consent) next.consent = 'Consent is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const intentLabel =
        formData.intent === 'demo' ? 'Private Demo Request' : 'Enterprise Inquiry';

      const payload: Record<string, string> = {
        'form-name': FORM_NAME,
        name: formData.name.trim(),
        company: formData.company.trim(),
        email: formData.email.trim(),
        website: formData.website.trim(),
        companySize: formData.companySize,
        monthlyVisitors: formData.monthlyVisitors.trim(),
        productsBrandsLocations: formData.productsBrandsLocations.trim(),
        primaryUseCase: formData.primaryUseCase.trim(),
        requiredIntegrations: formData.requiredIntegrations.trim(),
        launchTimeline: formData.launchTimeline.trim(),
        budgetRange: formData.budgetRange,
        intent: intentLabel,
        consent: formData.consent ? 'yes' : 'no',
      };

      if (window.location.hostname === 'localhost') {
        await new Promise((resolve) => setTimeout(resolve, 800));
        console.log('Enterprise inquiry → Netlify Forms (dev):', payload);
        setIsSubmitted(true);
        return;
      }

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeNetlifyBody(payload),
      });

      if (!response.ok) {
        throw new Error(`Netlify Forms returned ${response.status}`);
      }
      setIsSubmitted(true);
    } catch (err) {
      console.error('Enterprise inquiry error:', err);
      setErrors({
        form: 'Submission failed. Please try again or email us directly.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-8 text-center">
        <CheckCircle className="mx-auto mb-4 h-10 w-10 text-white/70" />
        <h3 className="text-xl font-bold">Inquiry received</h3>
        <p className="mt-3 text-sm leading-relaxed text-white/55">
          Thanks — our enterprise team will follow up within 2 business days
          {formData.intent === 'demo' ? ' to schedule a private demo' : ''}.
        </p>
      </div>
    );
  }

  return (
    <form
      name={FORM_NAME}
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
      className="space-y-5"
      noValidate
    >
      <input type="hidden" name="form-name" value={FORM_NAME} />
      <p className="hidden" aria-hidden="true">
        <label>
          Don’t fill this out: <input name="bot-field" tabIndex={-1} autoComplete="off" />
        </label>
      </p>

      {errors.form && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {errors.form}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enterprise-name" className={labelClass}>
            Name <span className="text-white/40">*</span>
          </label>
          <input
            id="enterprise-name"
            name="name"
            type="text"
            autoComplete="name"
            className={fieldClass}
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <p className={errorClass}>{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="enterprise-company" className={labelClass}>
            Company <span className="text-white/40">*</span>
          </label>
          <input
            id="enterprise-company"
            name="company"
            type="text"
            autoComplete="organization"
            className={fieldClass}
            value={formData.company}
            onChange={handleChange}
          />
          {errors.company && <p className={errorClass}>{errors.company}</p>}
        </div>

        <div>
          <label htmlFor="enterprise-email" className={labelClass}>
            Work email <span className="text-white/40">*</span>
          </label>
          <input
            id="enterprise-email"
            name="email"
            type="email"
            autoComplete="email"
            className={fieldClass}
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <p className={errorClass}>{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="enterprise-website" className={labelClass}>
            Website
          </label>
          <input
            id="enterprise-website"
            name="website"
            type="url"
            placeholder="https://"
            autoComplete="url"
            className={fieldClass}
            value={formData.website}
            onChange={handleChange}
          />
        </div>

        <div>
          <label htmlFor="enterprise-company-size" className={labelClass}>
            Company size
          </label>
          <select
            id="enterprise-company-size"
            name="companySize"
            className={selectClass}
            value={formData.companySize}
            onChange={handleChange}
          >
            <option value="" className={optionClass}>
              Select size
            </option>
            {LIVING_WEBSITE_ENTERPRISE_COMPANY_SIZES.map((size) => (
              <option key={size} value={size} className={optionClass}>
                {size} employees
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="enterprise-visitors" className={labelClass}>
            Estimated monthly website visitors
          </label>
          <input
            id="enterprise-visitors"
            name="monthlyVisitors"
            type="text"
            placeholder="e.g. 50,000"
            className={fieldClass}
            value={formData.monthlyVisitors}
            onChange={handleChange}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="enterprise-scope" className={labelClass}>
            Number of products, brands, or locations
          </label>
          <input
            id="enterprise-scope"
            name="productsBrandsLocations"
            type="text"
            placeholder="e.g. 4 brands across 12 locations"
            className={fieldClass}
            value={formData.productsBrandsLocations}
            onChange={handleChange}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="enterprise-use-case" className={labelClass}>
            Primary use case <span className="text-white/40">*</span>
          </label>
          <textarea
            id="enterprise-use-case"
            name="primaryUseCase"
            rows={3}
            placeholder="What should the living website accomplish for your organization?"
            className={fieldClass}
            value={formData.primaryUseCase}
            onChange={handleChange}
          />
          {errors.primaryUseCase && <p className={errorClass}>{errors.primaryUseCase}</p>}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="enterprise-integrations" className={labelClass}>
            Required integrations
          </label>
          <textarea
            id="enterprise-integrations"
            name="requiredIntegrations"
            rows={2}
            placeholder="CRM, inventory, catalog, APIs, account systems…"
            className={fieldClass}
            value={formData.requiredIntegrations}
            onChange={handleChange}
          />
        </div>

        <div>
          <label htmlFor="enterprise-timeline" className={labelClass}>
            Desired launch timeline
          </label>
          <input
            id="enterprise-timeline"
            name="launchTimeline"
            type="text"
            placeholder="e.g. Q3 pilot, full launch in 6 months"
            className={fieldClass}
            value={formData.launchTimeline}
            onChange={handleChange}
          />
        </div>

        <div>
          <label htmlFor="enterprise-budget" className={labelClass}>
            Budget range <span className="text-white/40">*</span>
          </label>
          <select
            id="enterprise-budget"
            name="budgetRange"
            className={selectClass}
            value={formData.budgetRange}
            onChange={handleChange}
          >
            <option value="" className={optionClass}>
              Select range
            </option>
            {LIVING_WEBSITE_ENTERPRISE_BUDGET_RANGES.map((range) => (
              <option key={range} value={range} className={optionClass}>
                {range}
              </option>
            ))}
          </select>
          {errors.budgetRange && <p className={errorClass}>{errors.budgetRange}</p>}
        </div>

        <div className="sm:col-span-2">
          <div className="flex items-start gap-3">
            <input
              id="enterprise-consent"
              name="consent"
              type="checkbox"
              checked={formData.consent}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-white/30 bg-black"
            />
            <label htmlFor="enterprise-consent" className="text-sm text-white/55">
              I consent to SELF Labs collecting this information to evaluate an Enterprise Living
              Website engagement. <span className="text-white/40">*</span>
            </label>
          </div>
          {errors.consent && <p className={errorClass}>{errors.consent}</p>}
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.02] hover:bg-white/90 disabled:opacity-60 disabled:hover:scale-100"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Submitting…
          </>
        ) : (
          <>
            {intent === 'demo' ? 'Request a Private Demo' : 'Talk to Enterprise'}
            <Send className="h-4 w-4" />
          </>
        )}
      </button>
    </form>
  );
}
